import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  User,
  deleteUser,
  sendEmailVerification,
} from "firebase/auth";
import { auth, db, analytics } from "@/firebase";
import { logEvent } from "firebase/analytics";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { FirebaseError } from "@firebase/util";
import i18n from "../i18n";
import { toast } from "sonner";
import { Role } from "@/model/entities";
import { fetchUserRole } from "@/service/authService";

interface AuthState {
  user: User | null;
  userRole: Role | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  signup: (signupFormData: any) => Promise<void>;
  logout: () => Promise<void>;
}

function handleAuthError(error: FirebaseError) {
  const { code } = error;
  console.error("Error creating user or restaurant:", error);
  switch (code) {
    case "auth/email-already-in-use":
      return i18n.t("auth.incorrectEmailOrPassword");
    case "auth/invalid-email":
      return i18n.t("auth.incorrectEmailOrPassword");
    case "auth/operation-not-allowed":
      return i18n.t("auth.operationNotAllowed");
    case "auth/weak-password":
      return i18n.t("auth.weakPassword");
    case "auth/user-disabled":
      return i18n.t("auth.userDisabled");
    case "auth/user-not-found":
      return i18n.t("auth.userNotFound");
    case "auth/invalid-credential":
      return i18n.t("auth.invalidCredential");
    case "auth/wrong-password":
      return i18n.t("auth.incorrectEmailOrPassword");
    default:
      return i18n.t("auth.genericError");
  }
}

const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      userRole: null,
      loading: false,
      error: null,

      login: async (email, password) => {
        set({ loading: true, error: null });
        try {
          const userCredential = await signInWithEmailAndPassword(
            auth,
            email,
            password
          );

          // Check if email is verified
          if (!userCredential.user.emailVerified) {
            console.info("User email not verified:", userCredential.user.uid);
            logEvent(analytics, "auth_email_not_verified", {
              uid: userCredential.user.uid,
            });
            set({ error: i18n.t("auth.emailNotVerified") });
            toast.error(i18n.t("auth.emailNotVerified"));
            await sendEmailVerification(userCredential.user);
            await signOut(auth); // Sign out the user since email is not verified
            throw new Error(i18n.t("auth.emailNotVerified"));
          }

          // get user role from Firestore
          const userRole = await fetchUserRole(userCredential.user.uid);

          console.info(
            "Fetched user role: ",
            userRole,
            userCredential.user.uid
          );
          logEvent(analytics, "auth_login_success", {
            uid: userCredential.user.uid,
            role: userRole,
          });

          set({ user: userCredential.user, userRole });
        } catch (e: unknown) {
          let errorMessage = i18n.t("auth.genericError");
          if (e instanceof FirebaseError) {
            const error = e as FirebaseError;
            errorMessage = handleAuthError(error);
          }
          console.error("Login error:", e);
          logEvent(analytics, "auth_error", { errorMessage });
          set({ error: errorMessage, user: null, userRole: null });
          throw e;
        } finally {
          set({ loading: false });
        }
      },

      signup: async (signupFormData: any) => {
        set({ loading: true, error: null });
        const {
          email,
          password,
          restaurantName,
          address,
          phone,
          businessType,
          plan,
          role,
          ownerPhoneNumber,
          subdomain,
        } = signupFormData;

        try {
          // Create user in Firebase Authentication
          const userCredential = await createUserWithEmailAndPassword(
            auth,
            email,
            password
          );
          const user = userCredential.user;

          // Send email verification
          await sendEmailVerification(user);

          // Create user entity in Firestore
          const userRef = doc(db, "users", user.uid);
          await setDoc(userRef, {
            email,
            role,
            createdAt: new Date().toISOString(),
          });

          // Create restaurant entity in Firestore
          const restaurantRef = doc(db, "restaurants", user.uid);
          await setDoc(restaurantRef, {
            restaurantName,
            address,
            phone,
            businessType,
            ownerId: user.uid,
            plan,
            role,
            ownerPhoneNumber,
            subdomain,
            createdAt: new Date().toISOString(),
          });

          set({ user, loading: false });
        } catch (e: unknown) {
          const error = e as FirebaseError;
          console.error("Error creating user or restaurant:", error);
          const errorMessage = handleAuthError(error);
          set({ error: errorMessage, loading: false });

          // Rollback: delete the user if Firestore operation fails
          if (auth.currentUser) {
            await deleteUser(auth.currentUser);
          }

          throw error; // Re-throw the error to handle it in the calling function
        } finally {
          set({ loading: false });
        }
      },

      logout: async () => {
        set({ loading: true });
        try {
          await signOut(auth);
          set({ user: null });
        } catch (e: unknown) {
          const error = e as Error;
          set({ error: error.message });
        } finally {
          set({ loading: false }); // ✅ Always reset loading
        }
      },

      getUserRole: async () => {
        set({ loading: true });
        try {
          const user = auth.currentUser;

          if (!user) {
            console.error("No user is currently signed in.");
            return null;
          }

          const userDoc = doc(db, "users", user.uid);
          const userSnapshot = await getDoc(userDoc);
          if (userSnapshot.exists()) {
            return userSnapshot.data().role as Role;
          } else {
            console.error("No such document!");
            return null;
          }
        } catch (e: unknown) {
          console.error("Error fetching user role:", e);
          return null;
        } finally {
          set({ loading: false });
        }
      },
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({ user: state.user, userRole: state.userRole }),
    }
  )
);

export default useAuthStore;
